import { useTheme } from '../context/ThemeContext';
import { ArrowRight, Check, DollarSign, Globe, Clock, Upload, Shield, Users, BarChart2, Lock, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import DspShowcase from '../components/DspShowcase';
import Metrics from '../components/Metrics';
import '../styles/HomePage.css';

const Home = () => {
  const { darkMode } = useTheme();

  return (
    <div className={`home-container ${darkMode ? 'dark' : ''}`}>
      {/* What is Falphin Section */}
      <section className="what-is">
        <div className="container">
          <div className="header-with-badge">
            <h1>What is Falphin?</h1>
            <span className="exclusive-badge">Exclusive</span>
          </div>
          <p className="lead">
            Falphin is an invite-only music distribution collective where only the most promising artists gain access to our premium platform and industry network.
          </p>
          <div className="what-is-grid">
            <div className="what-is-card">
              <h3>
                <span className="card-title-with-icon">
                  <Lock size={16} className="lock-icon" />
                  For Select Artists
                </span>
              </h3>
              <p>Distribute your music to all major platforms, track your streams, and get paid directly with our exclusive platform designed for serious artists who are ready to take their career to the next level.</p>
              <div className="exclusive-note">
                <Info size={14} />
                <span>Application and approval required • No payment until accepted</span>
              </div>
            </div>
            <div className="what-is-card">
              <h3>For Labels</h3>
              <p>Powerful tools to manage your roster, track releases, and analyze performance across all platforms from one dashboard.</p>
            </div>
            <div className="what-is-card">
              <h3>For Creators</h3>
              <p>Monetize your content fairly with our transparent royalty system that ensures you get paid what you deserve.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Exclusive Music Distribution for Select Artists</h1>
          <p className="hero-subtitle">
            Join an elite group of artists who get premium distribution, better visibility, and 
            direct access to industry opportunities. The best part? You only pay after you're accepted.
            <span className="highlight-feature">No upfront costs. No risk.</span>
          </p>
          <div className="cta-buttons">
            <Link to="/pricing" className="cta-button primary">
              Apply for Access <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="cta-button secondary">
              Contact Us
            </Link>
          </div>
          {/* Metrics Section */}
          <div className="metrics-section">
            <h3>Trusted by Artists Worldwide</h3>
            <Metrics />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="benefits-grid">
          {[
            { icon: <DollarSign size={24} />, text: 'No monetized channel required' },
            { icon: <Globe size={24} />, text: 'Earnings come from music rights' },
            { icon: <DollarSign size={24} />, text: 'Revenue collected globally' },
            { icon: <Clock size={24} />, text: 'Paid automatically' }
          ].map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-icon">{benefit.icon}</div>
              <p>{benefit.text}</p>
            </div>
          ))}
        </div>
        <p className="benefits-tagline">
          <strong>You focus on creating.</strong> We handle the infrastructure.
        </p>
      </section>

      {/* Why Choose Us */}
      <section className="why-us">
        <h2>Why Artists Choose Falphin</h2>
        <p className="section-intro">
          We're not just another distribution service. We're partners in your success, offering tools and 
          support that grow with your career.
        </p>
        <div className="features-grid">
          {[
            'One-time account creation fee',
            'Upload forever — no yearly payments',
            'Manual review & approval',
            'Secure royalty handling',
            'Built for artists & labels, not spam',
            'Scalable for serious growth'
          ].map((feature, index) => (
            <div key={index} className="feature-item">
              <Check size={20} className="check-icon" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </section>

      {/* DSP Showcase */}
      <DspShowcase />

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <h2>What Our Artists Say</h2>
          <p className="section-intro">
            Don't just take our word for it. Here's what artists and labels using Falphin have to say about their experience.
          </p>
          <div className="testimonials-grid">
            {[
              {
                quote: "Falphin's distribution service is next level! Their team helped me reach new audiences and maximize my streams across all platforms.",
                author: "Jumper Keellu",
                role: "Producer & Artist",
                avatar: `${process.env.PUBLIC_URL}/images/testimonials/jumper-keellu.png`
              },
              {
                quote: "Falphin has completely transformed how I distribute my music. Their platform makes it so easy to get my tracks on all major platforms, and the analytics help me understand my audience better. Couldn't be happier with the results!",
                author: "Sh-1nz",
                role: "Artist",
                avatar: `${process.env.PUBLIC_URL}/images/testimonials/sh-1nz.png`
              },
              {
                quote: "Falphin's auto-splits feature has been a game-changer for our label. It streamlines royalty distribution and ensures our artists get paid accurately and on time. The transparency and efficiency have significantly improved our operations.",
                author: "Amanda S.",
                role: "A&R",
                avatar: `${process.env.PUBLIC_URL}/images/testimonials/Amanda.png`
              }
            ].map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-content">
                  <div className="quote-icon">"</div>
                  <p className="testimonial-text">{testimonial.quote}</p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.author} 
                      className="avatar-image"
                      loading="lazy"
                    />
                  </div>
                    <div>
                      <h4>{testimonial.author}</h4>
                      <p className="testimonial-role">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="trusted-by">
        <div className="container">
          <h3>Trusted By Leading Artists & Labels</h3>
          <div className="companies-grid">
            {[
              { name: 'Harmony Records', logo: '🎵' },
              { name: 'Urban Beats', logo: '🎧' },
              { name: 'Indie Vibes', logo: '🎸' },
              { name: 'Sonic Wave', logo: '🎶' },
              { name: 'Beat Masters', logo: '🥁' },
              { name: 'Melody Makers', logo: '🎼' }
            ].map((company, index) => (
              <div key={index} className="company-logo" title={company.name}>
                <span className="company-emoji">{company.logo}</span>
                <span className="company-name">{company.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Your Music, Your Control</h2>
        <p>
          Falphin gives you the power to distribute your music worldwide while keeping full control 
          over your rights and creative direction. Our platform is built for artists who are serious 
          about their music and their business.
        </p>
        <div className="cta-tagline">
          <p>No subscriptions. No automatic sign-ups. No low-quality uploads.</p>
        </div>
        <Link to="/pricing" className="cta-button primary large">
          Start Your Journey <ArrowRight size={20} />
        </Link>
      </section>

      {/* Features */}
      <section className="features">
        <h2>Everything You Need to Succeed</h2>
        <div className="features-container">
          {[
            {
              icon: <Upload size={32} />,
              title: 'Easy Uploads',
              description: 'Simple and intuitive interface for uploading your music to all major platforms.'
            },
            {
              icon: <DollarSign size={32} />,
              title: 'Maximize Earnings',
              description: 'Keep more of your revenue with our competitive pricing and transparent fees.'
            },
            {
              icon: <Shield size={32} />,
              title: 'Secure & Reliable',
              description: 'Your music is protected with industry-standard security measures.'
            },
            {
              icon: <Users size={32} />,
              title: 'Artist Support',
              description: 'Dedicated support team to help you grow your music career.'
            },
            {
              icon: <BarChart2 size={32} />,
              title: 'Analytics',
              description: 'Track your streams, downloads, and earnings in real-time.'
            },
            {
              icon: <Globe size={32} />,
              title: 'Global Distribution',
              description: 'Reach listeners on all major streaming platforms worldwide.'
            }
          ].map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;